import { Separator } from "@/components/ui/separator";
import {
  ConfigurationTypeFile,
  FieldConfig,
  FormLayoutElement,
  isColLayoutElement,
  isLayoutElement,
  isRowLayoutElement,
} from "@/config/forms/general";
import { FormFieldVisibilityProvider } from "@/context/FormFieldVisibilityContext";
import React, { memo, useCallback } from "react";
import ImageField from "../form-fields/ImageField";
import InputSelectField from "../form-fields/InputSelectField";
import MultiSelectField from "../form-fields/MultiSelectField";
import RadioGroupField from "../form-fields/RadioGroupField";
import RatingField from "../form-fields/RatingField";
import SetField from "../form-fields/SetField";
import SwitchField from "../form-fields/SwitchField";
import TableFileField from "../form-fields/TableField";
import { MaskableFormFieldWrapper } from "../form-fields/MaskableFormFieldWrapper";
import {
  Checkbox,
  Col,
  LegacySelectCurrency,
  RichText,
  Row,
  Text,
} from "../form-items/AbstractForm";

interface GenericFormProps {
  version?: string;
  getValues: (data: any) => any;
  control?: any;
  positionImage?: string;
  configuration: ConfigurationTypeFile;
  className?: string;
  id?: string;
  /** Persists field visibility via UserConfig (form-fields.{formKey}). */
  formKey?: string;
}

const GenericForm = memo(
  ({
    className,
    getValues,
    version,
    control,
    configuration,
    positionImage,
    id,
    formKey,
  }: GenericFormProps) => {
    const renderField = useCallback(
      (configField: FieldConfig) => {
        const maskProps = {
          nonMaskable: configField.nonMaskable,
          maskable: configField.maskable,
        };
        const props = {
          config: configField,
          control,
          getValues,
          positionImage,
        };

        switch (configField.type) {
          case "text":
            return (
              <Text
                name={configField.name}
                disabled={configField.disabled}
                expandOnOverflow={configField.expandOnOverflow}
                description={configField.description}
                {...maskProps}
              />
            );
          case "richtext":
            return (
              <RichText
                name={configField.name}
                disabled={configField.disabled}
                {...maskProps}
              />
            );
          case "switch":
            return <SwitchField {...props} />;
          case "checkbox":
            return <Checkbox name={configField.name} {...maskProps} />;
          case "link-simple":
          case "multiSelect":
            return <MultiSelectField {...props} />;
          case "rating":
            return <RatingField {...props} />;
          case "inputSelect":
            return <InputSelectField {...props} />;
          case "inputSelectCurrency":
            return <LegacySelectCurrency name={configField.name} />;
          case "image":
            return <ImageField {...props} />;
          case "set":
            return <SetField {...props} />;
          case "radioGroup":
            return <RadioGroupField {...props} />;
          case "table-file":
            return (
              <MaskableFormFieldWrapper
                fieldName={configField.name}
                label={configField.label ?? configField.name}
                {...maskProps}
              >
                <TableFileField {...props} docId={id ?? ""} />
              </MaskableFormFieldWrapper>
            );
          case "separator":
            return <Separator className={`my-4 ${configField.className}`} />;
          default:
            return null;
        }
      },
      [control, getValues, positionImage, id],
    );

    const renderLayout = useCallback(
      (elements: FormLayoutElement[], keyPrefix: string): React.ReactNode => {
        return elements.map((el, index) => {
          const key = `${keyPrefix}-${index}`;
          if (isRowLayoutElement(el)) {
            return (
              <Row key={key} className={el.className}>
                {renderLayout(el.children, key)}
              </Row>
            );
          }
          if (isColLayoutElement(el)) {
            return (
              <Col key={key} className={el.className}>
                {renderLayout(el.children, key)}
              </Col>
            );
          }
          const field = el as FieldConfig;
          return (
            <div key={key} className={field.className}>
              {renderField(field)}
            </div>
          );
        });
      },
      [renderField],
    );

    const hasLayout = configuration.fields.some(isLayoutElement);

    const formBody = (
      <>
        {version && (
          <p className="text-[#0c111d] text-lg font-semibold font-['Raleway'] leading-normal text-start mb-5">
            {version}
          </p>
        )}
        {hasLayout ? (
          <div className="inline-flex flex-col gap-5 w-full">
            {renderLayout(configuration.fields, "layout")}
          </div>
        ) : (
          <div className="inline-flex justify-between items-center gap-2 flex-wrap">
            {configuration.fields.map((configField, index) => (
              <div
                key={`${(configField as FieldConfig).name}-${index}`}
                className={(configField as FieldConfig).className}
              >
                {renderField(configField as FieldConfig)}
              </div>
            ))}
          </div>
        )}
      </>
    );

    return (
      <form
        className={`w-full inline-flex flex-col justify-start ${className}`}
      >
        {formKey ? (
          <FormFieldVisibilityProvider formKey={formKey}>
            {formBody}
          </FormFieldVisibilityProvider>
        ) : (
          formBody
        )}
      </form>
    );
  },
);

GenericForm.displayName = "GenericForm";
export default GenericForm;
